import { agentReferenceMetadataToTitle, getCaseStatusFromReference } from '@/agent/references/util/agent-reference-title'
import { selectActiveContentViewerContextMatches } from '@/content-viewer/store/selectors'
import { ViewerOpenContextKind } from '@/content-viewer/store/schemas'
import { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { RiaSourceTriggerLocation } from '@/analytics/enums/ria.enums'
import { CitatorPanelSource, CitatorSurface } from '@/analytics/enums/citator.enums'
import { trackCitatorPanelOpened } from '@/analytics/utils/citator-analytics'
import { selectAgentConversationMatterId } from '@/agent/conversations/store/selectors'
import { RootState } from '@/store/store'
import { useAppSelector } from '@/store/store-hooks'
import { CaseStatusInfoTag } from '@/citator/components/CaseStatusInfoTag'
import { isDisplayableCaseStatus } from '@/citator/schema/case-status-schema'
import AgentResponseReferencesListItemHighlightButton from './AgentResponseReferencesListItemHighlightButton'
import ReferenceMetadataSummary from '@/agent/references/components/ReferenceMetadataSummary'
import { useOpenAgentReferenceViewer } from '@/agent/references/hooks/useOpenAgentReferenceViewer'
import { XClear } from '@/assets/icons'
import type { AgentReferenceDisplayGroup } from '@/agent/references/util/agent-reference-footnotes'

type AgentResponseReferencesListItemProps = {
  referenceGroup: AgentReferenceDisplayGroup
}

export default function AgentResponseReferencesListItem(props: AgentResponseReferencesListItemProps) {
  const { referenceGroup } = props
  const { trackEvent } = useAnalytics()
  const { openReference, isOpening, error, clearError } = useOpenAgentReferenceViewer()
  const primaryCitation = referenceGroup.citations[0]
  const displayCitationId = referenceGroup.displayCitationId

  // Watch whether this is the active reference
  const isActive = useAppSelector((state: RootState) =>
    referenceGroup.citations.some((citation) =>
      selectActiveContentViewerContextMatches(state, { kind: ViewerOpenContextKind.CHAT_CITATION, citationId: citation.citationId })
    )
  )

  // Get this reference
  const referencesByCitationId = useAppSelector((state: RootState) => state.agentReferencesState.references)
  const reference = referencesByCitationId[displayCitationId]
  const matterId = useAppSelector((state: RootState) =>
    reference ? selectAgentConversationMatterId(state, { conversationId: reference.conversation_id }) : null
  )

  if (!reference || !primaryCitation) {
    return null
  }

  const openReferenceInViewer = async (
    triggerLocation: RiaSourceTriggerLocation,
    citationId: string,
    citationHighlightIndex?: number,
    defaultTab?: CaseLawReferenceTab
  ): Promise<boolean> => {
    const targetReference = referencesByCitationId[citationId]
    if (!targetReference) return false

    return openReference({ reference: targetReference, matterId, triggerLocation, citationHighlightIndex, defaultTab })
  }

  const displayHighlights = referenceGroup.highlights

  const handleViewReferenceTitle = () => {
    const firstHighlight = displayHighlights[0]
    if (firstHighlight) {
      void openReferenceInViewer(RiaSourceTriggerLocation.ReferencesListItem, firstHighlight.citationId, firstHighlight.citationHighlightIndex)
      return
    }

    void openReferenceInViewer(RiaSourceTriggerLocation.ReferencesListItem, displayCitationId)
  }

  const handleViewHighlight = (highlight: AgentReferenceDisplayGroup['highlights'][number]) => {
    void openReferenceInViewer(RiaSourceTriggerLocation.ReferencesHighlight, highlight.citationId, highlight.citationHighlightIndex)
  }

  const title = agentReferenceMetadataToTitle(reference)
  const caseStatus = getCaseStatusFromReference(reference)

  // Open the reference panel directly to the citation analysis (citator) tab.
  const handleViewCitationAnalysis = async () => {
    const opened = await openReferenceInViewer(RiaSourceTriggerLocation.ReferencesListItem, displayCitationId, undefined, CaseLawReferenceTab.citator)

    // CaseStatusInfoTag owns CitatorBadgeClicked; this CTA only opens the panel.
    if (opened && isDisplayableCaseStatus(caseStatus)) {
      trackCitatorPanelOpened({
        trackEvent,
        surface: CitatorSurface.Chat,
        source: CitatorPanelSource.BadgeClick,
      })
    }
  }

  return (
    <div className={`flex flex-col gap-1 text-left w-full p-2 rounded-md ${isActive ? 'bg-brand-neutral-100' : 'hover:bg-brand-neutral-50'}`}>
      <button className={'text-left flex flex-col gap-1 disabled:cursor-wait'} disabled={isOpening} onClick={handleViewReferenceTitle}>
        <div className={'compatible-word-break text-sm font-medium text-brand-700 underline hover:text-brand-800 cursor-pointer'}>
          [{referenceGroup.footnoteNumber}]&nbsp;{title}
        </div>
        <div className="text-xs text-brand-neutral-950 leading-4">
          <ReferenceMetadataSummary reference={reference} />
        </div>
      </button>
      {/* Render Highlight Links */}
      {displayHighlights.length > 0 && (
        <div className={'flex gap-x-1 text-xs leading-4 text-brand-neutral-950'}>
          <div className={'whitespace-nowrap'}>Highlights: </div>
          <div className={'flex flex-wrap gap-1'}>
            {displayHighlights.map((highlight) => (
              <AgentResponseReferencesListItemHighlightButton
                key={`${highlight.citationId}_${highlight.citationHighlightIndex}`}
                citation_id={highlight.citationId}
                citationHighlightIndex={highlight.citationHighlightIndex}
                citationLabel={highlight.citationLabel}
                disabled={isOpening}
                onClick={() => handleViewHighlight(highlight)}
              />
            ))}
          </div>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-1 text-xs font-medium text-red-800">
          <span className="min-w-0">{error}</span>
          <button
            type="button"
            aria-label="Dismiss reference error"
            onClick={clearError}
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-red-800 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          >
            <XClear className="h-3 w-3" />
          </button>
        </div>
      )}
      {isDisplayableCaseStatus(caseStatus) && (
        // `trackImpression={false}`: per-badge impressions are replaced by the
        // group-level CitatorReferenceGroupExpanded event, but the badge click
        // (CitatorBadgeClicked) is still tracked via `surface`.
        <CaseStatusInfoTag
          status={caseStatus}
          onViewCitationAnalysis={handleViewCitationAnalysis}
          className={displayHighlights.length > 0 ? 'mt-1' : undefined}
          surface={CitatorSurface.Chat}
          trackImpression={false}
        />
      )}
    </div>
  )
}
